import { Link, useRouterState } from "@tanstack/react-router";
import {
  Folder,
  Forward,
  Activity,
  HardDrive,
  type LucideIcon,
  LogOut,
  Settings,
  Share2,
  Shield,
  Trash2,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useStorageSummary } from "@/api/storage";
import { logout } from "@/auth/login";
import { useAuth } from "@/auth/stub";
import { Logo } from "@/components/Logo";
import { ReportProblem } from "@/components/ReportProblemDialog";
import { useAppName } from "@/lib/branding";
import { formatBytes } from "@/lib/format";
import { cn } from "@/lib/utils";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    parts.length > 1 ? parts[0]![0]! + parts.at(-1)![0]! : name.slice(0, 2);
  return letters.toUpperCase();
}

function NavItem({
  to,
  icon: Icon,
  active,
  children,
}: {
  to: string;
  icon: LucideIcon;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex h-9 items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ochre",
        active
          ? "bg-white/10 font-medium text-sidebar-active"
          : "text-sidebar-foreground hover:bg-white/5 hover:text-sidebar-active",
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {children}
    </Link>
  );
}

function StorageMeter() {
  const { t } = useTranslation();
  const summary = useStorageSummary();
  if (!summary.data) return null;
  const {
    used_bytes: used,
    quota_bytes: quota,
    trash_bytes: trash,
  } = summary.data;
  const pct = quota ? Math.min(100, Math.round((used / quota) * 100)) : null;
  // Point at the trash once it is a noticeable share of the usage:
  // people don't expect deleted files to still take space.
  const showTrash = trash > 0 && trash >= used * 0.05;
  return (
    <Link
      to="/storage"
      aria-label={t("storage.details")}
      className="block space-y-1.5 rounded-md px-2.5 py-1 text-xs hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ochre"
    >
      <div className="flex justify-between text-sidebar-foreground">
        <span>{t("storage.title")}</span>
        {pct !== null && <span className="text-sidebar-active">{pct}%</span>}
      </div>
      {pct !== null && (
        <div
          role="progressbar"
          aria-label={t("storage.title")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          className="h-1 overflow-hidden rounded bg-white/15"
        >
          <div
            className="h-1 rounded bg-brand-ochre"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <div className="text-sidebar-muted">
        {quota === null
          ? t("storage.used_unlimited", { used: formatBytes(used) })
          : t("storage.used_of", {
              used: formatBytes(used),
              quota: formatBytes(quota),
            })}
      </div>
      {showTrash && (
        <div className="text-sidebar-muted">
          {t("storage.meter_trash", { size: formatBytes(trash) })}
        </div>
      )}
    </Link>
  );
}

/** Main navigation: a dark column on wide screens, a drawer on phones. */
export function AppSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const appName = useAppName();
  const user = useAuth((s) => s.user);
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (!user) return null;

  const filesActive =
    path === "/" || path.startsWith("/folder/") || path.startsWith("/view/");
  return (
    <nav
      aria-label={t("nav.main")}
      className="flex h-full w-56 flex-col gap-0.5 bg-sidebar px-2.5 py-4 text-sidebar-foreground"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a")) onNavigate?.();
      }}
    >
      <Link
        to="/"
        className="mb-4 flex items-center gap-2.5 rounded-md px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ochre"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white">
          <Logo className="h-5 w-5" />
        </span>
        <span className="text-base font-semibold text-sidebar-active">
          {appName}
        </span>
      </Link>

      <NavItem to="/" icon={Folder} active={filesActive}>
        {t("nav.my_files")}
      </NavItem>
      <NavItem
        to="/shared-with-me"
        icon={Share2}
        active={path === "/shared-with-me"}
      >
        {t("nav.shared_with_me")}
      </NavItem>
      <NavItem
        to="/shared-by-me"
        icon={Forward}
        active={path === "/shared-by-me"}
      >
        {t("nav.shared_by_me")}
      </NavItem>
      <NavItem to="/activity" icon={Activity} active={path === "/activity"}>
        {t("nav.activity")}
      </NavItem>
      <NavItem to="/trash" icon={Trash2} active={path === "/trash"}>
        {t("nav.trash")}
      </NavItem>

      <div className="mx-2.5 mb-1.5 mt-4 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
        {t("nav.account")}
      </div>
      <NavItem to="/storage" icon={HardDrive} active={path === "/storage"}>
        {t("nav.storage")}
      </NavItem>
      <NavItem to="/security" icon={Shield} active={path === "/security"}>
        {t("nav.security")}
      </NavItem>
      {user.is_admin && (
        <NavItem to="/admin" icon={Settings} active={path === "/admin"}>
          {t("nav.administration")}
        </NavItem>
      )}

      <div className="flex-1" />
      <ReportProblem className="mb-3" />
      <StorageMeter />
      <div className="mt-3 flex items-center gap-2.5 border-t border-white/10 px-2 pt-3">
        <span
          aria-hidden
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-ochre text-[11px] font-semibold text-[#1A2230]"
        >
          {initials(user.display_name || user.email)}
        </span>
        <span className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate text-sm font-medium text-sidebar-active">
            {user.display_name}
          </span>
          <span className="truncate text-[11px] text-sidebar-muted">
            {user.email}
          </span>
        </span>
        <button
          type="button"
          onClick={() => void logout()}
          aria-label={t("auth.logout")}
          className="flex h-8 w-8 items-center justify-center rounded-md text-sidebar-foreground hover:bg-white/5 hover:text-sidebar-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ochre"
        >
          <LogOut className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </nav>
  );
}
